import { API } from "@swivl-mono/api-helper";
import { VarsHelper } from "@swivl-mono/helpers";
import { LocationVarInheritance, LocationVarInterface, LocationVarKey, VarDef, VarType } from "@swivl-mono/types";
import { TextArea } from "@swivl-mono/ui";
import { useCallback, useEffect, useState } from "react";

import { SettingRow, SettingsCard } from "../../../Components/SettingsCard/SettingsCard";
import { SearchableLabel } from "../../../Components/Worksheets/Types/Worksheet.Interface";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import { revertLabelFor } from "../../../Views/Properties/CortexV3/Components/revertTarget";
import { WidgetSwitch } from "../../../Views/Widgets/WidgetSwitch";

/**
 * Admin page — settings managed by Swivl staff, hidden from operators.
 * Registered with `swivlStaffOnly: true` in DetailsV3.Worksheets.Data.tsx;
 * the sidebar + router filter the page out for non-staff accounts.
 *
 * Laid out like Facility Info: one card per group of settings, read-only until
 * the card's Edit is clicked, each row showing where its value comes from.
 */

export const PAGE_KEYS: LocationVarKey[] = [
  LocationVarKey.CortexVoicePreWelcomeMessage,
  LocationVarKey.CortexIsSMSCampaignsEnabled,
  LocationVarKey.CortexTenantPageLinksEnabled
];

// Hand-authored — rows carry live handlers, can't be data-driven.
// swivlStaffOnly comes from the page registration, not repeated here.
export const SEARCH_LABELS: SearchableLabel[] = [
  { title: "Admin", snippet: "Settings managed by Swivl staff." },
  { title: "Voice", snippet: "What callers hear before the bot speaks." },
  { title: "Voice pre-welcome message" },
  { title: "SMS Campaigns", snippet: "Org-wide capability, no group or location overrides." },
  { title: "SMS campaigns", snippet: 'Gates the "New SMS" campaign button.' },
  { title: "Tenant page", snippet: "Whether payment links open the spot tenant page." },
  { title: "Payment links open the tenant page", snippet: "Past-due texts, widget Pay Now and the bot's pay action." }
];

type ScopeKind = "org" | "group" | "location";

/** The built-in default for a var, the same source the server merges from. */
function varDefault(varKey: LocationVarKey): string {
  return VarDef[VarsHelper.varKeyForType(VarType.Location, varKey)]?.defaultValue ?? "";
}

interface Resolved {
  /** Scope owning the winning row, or undefined when nothing is set anywhere. */
  ownerScope: ScopeKind | undefined;
  /** The row at the CURRENT scope, if any — what a revert would delete. */
  ownRow: LocationVarInterface | null;
  value: string;
  /** What would win if the current scope's row were deleted. */
  parentScope: ScopeKind | undefined;
  parentValue: string;
}

/**
 * Walk location → group → org and report which scope wins, plus what would win
 * without the current scope's row.
 *
 * The previous version looked only for the org row, so a group override was
 * invisible at a location and the page could disagree with what the bot
 * actually resolves.
 */
function resolveCascade(
  rows: LocationVarInterface[],
  scopeKind: ScopeKind,
  groupId: number | undefined,
  locationId: number | undefined,
  fallback: string
): Resolved {
  const orgRow = rows.find((r) => (r.groupId ?? null) === null && (r.locationId ?? null) === null) ?? null;
  const groupRow = groupId != null ? (rows.find((r) => (r.locationId ?? null) === null && r.groupId === groupId) ?? null) : null;
  const locationRow = locationId != null ? (rows.find((r) => r.locationId === locationId) ?? null) : null;

  // Most-specific first, starting at the scope being viewed — a location never
  // resolves from another location, and org sees only its own row.
  const chain: [ScopeKind, LocationVarInterface | null][] =
    scopeKind === "location"
      ? [
          ["location", locationRow],
          ["group", groupRow],
          ["org", orgRow]
        ]
      : scopeKind === "group"
        ? [
            ["group", groupRow],
            ["org", orgRow]
          ]
        : [["org", orgRow]];

  const winner = chain.find(([, row]) => !!row?.value);
  const parent = chain.slice(1).find(([, row]) => !!row?.value);

  return {
    ownerScope: winner?.[0],
    ownRow: chain[0]![1],
    value: winner?.[1]?.value ?? fallback,
    parentScope: parent?.[0],
    parentValue: parent?.[1]?.value ?? fallback
  };
}

/** Map a resolved scope onto the shared inheritance vocabulary. */
function inheritanceFor(ownerScope: ScopeKind | undefined, scopeKind: ScopeKind): LocationVarInheritance {
  if (ownerScope === undefined) return LocationVarInheritance.DEFAULT;
  if (ownerScope === scopeKind) return LocationVarInheritance.NONE;
  return ownerScope === "group" ? LocationVarInheritance.GROUP : LocationVarInheritance.ORG;
}

export default function AdminPage(): React.ReactElement {
  const page = usePropertiesGetLocationIds();
  const orgId = Member.orgId;
  const scopeKind: ScopeKind = page.locationId != null ? "location" : page.groupId != null ? "group" : "org";
  // SMS campaigns is an ORG capability — the card only renders at org scope
  // (no group/location overrides; Chuck, 2026-07-27 / ENG-2946).
  const isOrgScope = scopeKind === "org";

  const [voiceRows, setVoiceRows] = useState<LocationVarInterface[]>([]);
  const [smsRow, setSmsRow] = useState<LocationVarInterface | null>(null);
  const [tenantPageRows, setTenantPageRows] = useState<LocationVarInterface[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      // `getAllVariablesForKey` returns rows across all scopes for the org.
      // The location-keyed sibling `getAllLocationVariables` filters to
      // `locationId IS NOT NULL` and silently drops org/group rows — wrong fit.
      const [voice, sms, tenantPage] = await Promise.all([
        API.LocationVars.getAllVariablesForKey(orgId, LocationVarKey.CortexVoicePreWelcomeMessage),
        API.LocationVars.getAllVariablesForKey(orgId, LocationVarKey.CortexIsSMSCampaignsEnabled),
        API.LocationVars.getAllVariablesForKey(orgId, LocationVarKey.CortexTenantPageLinksEnabled)
      ]);
      setVoiceRows(voice);
      setTenantPageRows(tenantPage);
      setSmsRow(sms.find((r) => (r.groupId ?? null) === null && (r.locationId ?? null) === null) ?? null);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, [orgId]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  if (!orgId) return <div className="p-6 text-sm text-grey-500">No active org.</div>;

  return (
    <div className="mx-auto flex max-w-[960px] flex-col gap-4 p-6">
      {/* Mirrors CortexTopicWorksheet's page header. No mb-4 — the container's
          gap-4 already provides that spacing. */}
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="h-3 w-1 shrink-0 bg-grey-900" />
            <h2 className="text-base font-semibold text-grey-900">Admin</h2>
          </div>
          <p className="pl-3 text-base text-grey-600">Settings managed by swivl staff. Operators don&apos;t see this page.</p>
        </div>
      </header>

      {loadError && <p className="body-sm rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">Failed: {loadError}</p>}

      <VoiceCard
        orgId={orgId}
        rows={voiceRows}
        scopeKind={scopeKind}
        groupId={page.groupId}
        locationId={page.locationId}
        scopeTitle={page.typeTitle}
        onSaved={load}
      />

      {isOrgScope && <SMSCampaignsCard orgId={orgId} row={smsRow} onSaved={load} />}

      <TenantPageCard
        orgId={orgId}
        rows={tenantPageRows}
        scopeKind={scopeKind}
        groupId={page.groupId}
        locationId={page.locationId}
        scopeTitle={page.typeTitle}
        onSaved={load}
      />
    </div>
  );
}

// ─── Voice ─────────────────────────────────────────────────────────────────

interface VoiceCardProps {
  orgId: number;
  rows: LocationVarInterface[];
  scopeKind: ScopeKind;
  groupId: number | undefined;
  locationId: number | undefined;
  scopeTitle: string;
  onSaved: () => Promise<void>;
}

function VoiceCard({ orgId, rows, scopeKind, groupId, locationId, scopeTitle, onSaved }: VoiceCardProps): React.ReactElement {
  const fallback = varDefault(LocationVarKey.CortexVoicePreWelcomeMessage);
  const resolved = resolveCascade(rows, scopeKind, groupId, locationId, fallback);

  const [draft, setDraft] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const display = draft ?? resolved.value;
  const ownsHere = resolved.ownerScope === scopeKind;

  const handleEdit = (): void => {
    setDraft(resolved.value);
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setDraft(null);
    setIsEditing(false);
  };

  const handleSave = async (): Promise<void> => {
    if (draft === null) return;
    setSaving(true);
    setError(null);
    try {
      if (resolved.ownRow) await API.LocationVars.updateVariable(orgId, resolved.ownRow.id, draft);
      else await API.LocationVars.createVariable(LocationVarKey.CortexVoicePreWelcomeMessage, draft, orgId, groupId, locationId);
      await onSaved();
      setDraft(null);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (): Promise<void> => {
    if (!resolved.ownRow) return;
    if (!window.confirm("Revert? This discards the customization at this scope.")) return;
    setSaving(true);
    try {
      await API.LocationVars.deleteVariable(orgId, resolved.ownRow.id);
      await onSaved();
      setDraft(null);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const revertLabel = revertLabelFor({
    isOrgScope: scopeKind === "org",
    isOverride: ownsHere,
    inheritance: inheritanceFor(resolved.ownerScope, scopeKind),
    parentInheritance: inheritanceFor(resolved.parentScope, scopeKind)
  });

  return (
    <SettingsCard
      title="Voice"
      caption="What callers hear before the bot speaks."
      isEditing={isEditing}
      saving={saving}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
    >
      {error && <p className="body-sm mx-4 my-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">{error}</p>}
      <SettingRow
        label="Voice pre-welcome message"
        description={`Played to voice callers before the bot greeting, and required for call-recording compliance in many jurisdictions. Saving sets the override at the current scope (${scopeTitle}).`}
        inheritance={inheritanceFor(resolved.ownerScope, scopeKind)}
        isEditing={isEditing}
        revert={ownsHere ? { label: revertLabel, revertsTo: resolved.parentValue || "(empty)", onRevert: handleRevert } : undefined}
      >
        {isEditing ? (
          <TextArea className="block" value={display} rows={3} placeholder={fallback} onChange={(e) => setDraft(e.target.value)} />
        ) : (
          <p className="body-sm break-words whitespace-pre-wrap text-grey-900">{display || "—"}</p>
        )}
      </SettingRow>
    </SettingsCard>
  );
}

// ─── SMS campaigns ─────────────────────────────────────────────────────────

interface SMSCampaignsCardProps {
  orgId: number;
  row: LocationVarInterface | null;
  onSaved: () => Promise<void>;
}

function isTruthy(value: string | undefined): boolean {
  return value === "1" || value === "true";
}

function SMSCampaignsCard({ orgId, row, onSaved }: SMSCampaignsCardProps): React.ReactElement {
  const fallback = varDefault(LocationVarKey.CortexIsSMSCampaignsEnabled);
  const saved = row ? isTruthy(row.value) : isTruthy(fallback);

  const [draft, setDraft] = useState<boolean | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabled = draft ?? saved;

  const handleEdit = (): void => {
    setDraft(saved);
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setDraft(null);
    setIsEditing(false);
  };

  const handleSave = async (): Promise<void> => {
    if (draft === null) return;
    setSaving(true);
    setError(null);
    try {
      const next = draft ? "1" : "0";
      if (row) await API.LocationVars.updateVariable(orgId, row.id, next);
      else await API.LocationVars.createVariable(LocationVarKey.CortexIsSMSCampaignsEnabled, next, orgId, undefined, undefined);
      await onSaved();
      setDraft(null);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (): Promise<void> => {
    if (!row) return;
    if (!window.confirm("Revert to Default? This discards the org setting.")) return;
    setSaving(true);
    try {
      await API.LocationVars.deleteVariable(orgId, row.id);
      await onSaved();
      setDraft(null);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="SMS Campaigns"
      caption="Org-wide capability. No group or location overrides."
      isEditing={isEditing}
      saving={saving}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
    >
      {error && <p className="body-sm mx-4 my-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">{error}</p>}
      <SettingRow
        label="SMS campaigns"
        description={'Gates the "New SMS" campaign button in Contact Center - NEW.'}
        inheritance={row ? LocationVarInheritance.NONE : LocationVarInheritance.DEFAULT}
        isEditing={isEditing}
        revert={row ? { label: "Revert to Default", revertsTo: isTruthy(fallback) ? "On" : "Off", onRevert: handleRevert } : undefined}
      >
        {/* Always shown, dimmed outside edit mode — same as the Amenities rows.
            WidgetSwitch carries its own disabled styling. The wrapper is flex
            because the switch is inline-level and a block wrapper would give it
            a text line box taller than the control. */}
        <div className="flex items-center">
          <WidgetSwitch on={enabled} disabled={!isEditing} onSet={setDraft} ariaLabel="SMS campaigns enabled" />
        </div>
      </SettingRow>
    </SettingsCard>
  );
}

// ─── Tenant page (ENG-3545) ────────────────────────────────────────────────

interface TenantPageCardProps {
  orgId: number;
  rows: LocationVarInterface[];
  scopeKind: ScopeKind;
  groupId: number | undefined;
  locationId: number | undefined;
  scopeTitle: string;
  onSaved: () => Promise<void>;
}

/**
 * Whether payment links for this scope open the spot tenant page. Cascades org → group →
 * location like the voice card; on by default (plan D22). The org's `tenant_page` feature flag
 * is the kill switch above this (Feature Flags admin).
 */
function TenantPageCard({ orgId, rows, scopeKind, groupId, locationId, scopeTitle, onSaved }: TenantPageCardProps): React.ReactElement {
  const fallback = varDefault(LocationVarKey.CortexTenantPageLinksEnabled);
  const resolved = resolveCascade(rows, scopeKind, groupId, locationId, fallback);
  const saved = isTruthy(resolved.value);

  const [draft, setDraft] = useState<boolean | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabled = draft ?? saved;
  const ownsHere = resolved.ownerScope === scopeKind;

  const handleEdit = (): void => {
    setDraft(saved);
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setDraft(null);
    setIsEditing(false);
  };

  const handleSave = async (): Promise<void> => {
    if (draft === null) return;
    setSaving(true);
    setError(null);
    try {
      const next = draft ? "1" : "0";
      if (resolved.ownRow) await API.LocationVars.updateVariable(orgId, resolved.ownRow.id, next);
      else await API.LocationVars.createVariable(LocationVarKey.CortexTenantPageLinksEnabled, next, orgId, groupId, locationId);
      await onSaved();
      setDraft(null);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (): Promise<void> => {
    if (!resolved.ownRow) return;
    if (!window.confirm("Revert? This discards the customization at this scope.")) return;
    setSaving(true);
    try {
      await API.LocationVars.deleteVariable(orgId, resolved.ownRow.id);
      await onSaved();
      setDraft(null);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const revertLabel = revertLabelFor({
    isOrgScope: scopeKind === "org",
    isOverride: ownsHere,
    inheritance: inheritanceFor(resolved.ownerScope, scopeKind),
    parentInheritance: inheritanceFor(resolved.parentScope, scopeKind)
  });

  return (
    <SettingsCard
      title="Tenant page"
      caption="Whether payment links open the spot tenant page. The org's tenant_page feature flag must also be on."
      isEditing={isEditing}
      saving={saving}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
    >
      {error && <p className="body-sm mx-4 my-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">{error}</p>}
      <SettingRow
        label="Payment links open the tenant page"
        description={`When ON, past-due texts, the widget's Pay Now and the bot's pay action send tenants to their spot tenant page (units, balances, pay). When OFF they get the facility's Pay Online URL as before. Saving sets the override at the current scope (${scopeTitle}).`}
        inheritance={inheritanceFor(resolved.ownerScope, scopeKind)}
        isEditing={isEditing}
        revert={ownsHere ? { label: revertLabel, revertsTo: isTruthy(resolved.parentValue) ? "On" : "Off", onRevert: handleRevert } : undefined}
      >
        <div className="flex items-center">
          <WidgetSwitch on={enabled} disabled={!isEditing} onSet={setDraft} ariaLabel="Payment links open the tenant page" />
        </div>
      </SettingRow>
    </SettingsCard>
  );
}
